import { useEffect, useMemo, useRef } from 'react'

import { useFacilitySearchOptions } from '@/hooks/useFacilitySearchOptions'
import { useLightMode } from '@/hooks/useLightMode'
import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Box, Flex, Modal, Skeleton } from '@hivewatch/dark'

import {
  ControlledMultiSelectInput,
  ControlledSelect,
  ControlledTextInput,
} from '@/components/ui'
import {
  useStandardOperatingProcedureEditQuery,
  useUpdateStandardOperatingProcedureMutation,
} from '@/graphql/generated/hooks'
import { SopType } from '@/graphql/generated/schemas'
import {
  FacilityOptionWithAddressI,
  OptionI,
  getFacilityOptions,
} from '@/utils/forms/optionBuilders'
import { tVar } from '@/utils/i18n'

import { useFacilitySelectAll } from '../hooks'
import { SOP_ROLES } from '../utils/utils'
import { FacilitySelectHeader } from './FacilitySelectHeader'

interface EditSOPModalIProps {
  isOpen: boolean
  onClose: () => void
  sop: {
    id: string
    name: string
  }
  refetch: () => void
}

interface FormInputsIProps {
  title: string
  facilities?: OptionI[]
  role: OptionI<SopType>
}

export const EditSOPModal = ({
  isOpen,
  onClose,
  refetch,
  sop,
}: EditSOPModalIProps) => {
  const { t } = useTranslation()
  const colorScheme = useLightMode()
  const hasInitializedForm = useRef(false)
  const { shouldEnableUpdateSOP } = usePermissions()
  const { showSuccess, showError } = useToasts()
  const { handleSubmit, control, errors, setValue, formState, watch } =
    useForm<FormInputsIProps>({
      mode: 'onChange',
    })
  const { isDirty } = formState

  const { data: sopData, loading: isSopLoading } =
    useStandardOperatingProcedureEditQuery({
      variables: {
        id: sop?.id,
      },
      fetchPolicy: 'network-only',
    })
  const { facilityOptions: dropdownFacilityOptions, setFacilityName } =
    useFacilitySearchOptions()
  const isInitialLoading = isSopLoading || !sopData

  const [updateSOP, { loading: isUpdateSOPLoading }] =
    useUpdateStandardOperatingProcedureMutation()

  const allFacilityOptions = useMemo(
    () => getFacilityOptions(sopData),
    [sopData]
  )
  // ControlledMultiSelectInput's subtitle/caption are plain presentation
  // strings, so the "matched by address" cue is translated here rather than
  // baked into getIncidentFacilityOptionsWithAddress (which stays
  // i18n-agnostic).
  const dropdownFacilityOptionsWithCue = useMemo(
    () =>
      (dropdownFacilityOptions as FacilityOptionWithAddressI[]).map(
        (option) => ({
          ...option,
          caption:
            option.matchedField === 'address'
              ? t('common:facility_matched_by_address')
              : undefined,
        })
      ),
    [dropdownFacilityOptions, t]
  )

  // Use custom hook for Select All / Deselect All functionality
  const { areAllFacilitiesSelected, handleSelectAllFacilities } =
    useFacilitySelectAll({
      watch,
      setValue,
      facilityOptions: allFacilityOptions,
    })

  const onSubmit: SubmitHandler<FormInputsIProps> = async (values) => {
    try {
      await updateSOP({
        variables: {
          input: {
            id: sop?.id,
            type: values.role.value,
            facilityIds: (values.facilities ?? []).map(
              (facilityOption) => facilityOption.value
            ),
          },
        },
      })
      refetch?.()
      onClose()
      showSuccess(tVar(t, 'sops:crud_edit_success', { name: sop?.name }))
    } catch {
      showError()
    }
  }

  const isFormReadyToInitialize =
    !isSopLoading && !!sopData && !hasInitializedForm.current

  useEffect(() => {
    if (isFormReadyToInitialize) {
      hasInitializedForm.current = true
      const facilityIds = sopData.standardOperatingProcedure?.facilityIds ?? []
      const facilities = facilityIds
        .map((id) => allFacilityOptions.find((f) => f.value === id))
        .filter((facility): facility is OptionI => Boolean(facility))
      setValue('facilities', facilities, {
        shouldValidate: true,
      })
      const type = SOP_ROLES.find(
        (f) => f.value === sopData.standardOperatingProcedure?.type
      )
      setValue('role', type, {
        shouldValidate: true,
      })
    }
  }, [allFacilityOptions, isFormReadyToInitialize, setValue, sopData])

  return (
    <Modal
      bodyStyleOverrides={{ minH: 0, overflowY: 'auto' }}
      colorScheme={colorScheme}
      isOpen={isOpen}
      isSaveDisabled={
        isInitialLoading ||
        !hasInitializedForm.current ||
        !shouldEnableUpdateSOP ||
        !isDirty
      }
      isSaveLoading={isUpdateSOPLoading}
      maxW='600px'
      onClose={onClose}
      onSave={handleSubmit(onSubmit)}
      saveButtonLabel={t('common:save_changes')}
      showCancelButton
      size='compact'
      title={t('sops:crud_edit_sop')}
    >
      {isInitialLoading ? (
        <FormSkeleton />
      ) : (
        <Flex data-testid='EditSOPModal:form' direction='column' gap='16px'>
          <ControlledTextInput
            colorScheme={colorScheme}
            control={control}
            data-testid='EditSOPModal:title'
            defaultValue={sop.name}
            disabled
            errors={errors}
            label={t('sops:crud_sop_title_label')}
            name='title'
            width='100%'
          />

          <Box>
            <FacilitySelectHeader
              allFacilitiesSelected={areAllFacilitiesSelected}
              colorScheme={colorScheme}
              dataTestId='EditSOPModal:selectAllFacilities'
              isDisabled={allFacilityOptions.length === 0}
              onToggleSelectAll={handleSelectAllFacilities}
            />
            <ControlledMultiSelectInput
              colorScheme={colorScheme}
              control={control}
              data-testid='EditSOPModal:facilities'
              // The facility search is server-side and also matches by
              // address, so an address-only match's label may not contain
              // the typed query — 'internal' mode would re-filter it away.
              filterMode='external'
              maxDropdownHeight='200px'
              name='facilities'
              onQueryChange={setFacilityName}
              options={dropdownFacilityOptionsWithCue}
              placeholder={t('sops:crud_facilities_placeholder')}
              searchable
              usePortal
            />
          </Box>

          <ControlledSelect
            colorScheme={colorScheme}
            control={control}
            data-testid='CreateSOPModal:role'
            isDisabled={isSopLoading}
            label={t('sops:crud_available_to_label')}
            name='role'
            options={SOP_ROLES}
            placeholder={t('sops:crud_available_to_placeholder')}
            rules={{ required: true }}
          />
        </Flex>
      )}
    </Modal>
  )
}

const FormSkeleton = () => {
  return (
    <Box data-testid='EditSOPModal:skeleton'>
      <Skeleton h='15px' maxW='120px' mb='9px' />
      <Skeleton h='45px' mb='20px' />
      <Skeleton h='15px' maxW='120px' mb='9px' />
      <Skeleton h='45px' mb='20px' />
      <Skeleton h='15px' maxW='120px' mb='9px' />
      <Skeleton h='45px' mb='20px' />
    </Box>
  )
}
